/**
 * Atlas PDF Flipbook — portable PDF.js + page-flip viewer.
 *
 * Copy with:
 *   assets/atlas-pdf-flipbook.js
 *   assets/atlas-pdf-flipbook.css
 *   snippets/atlas-pdf-flipbook.liquid
 *
 * Usage:
 *   var viewer = AtlasPdfFlipbook.mount(rootEl);
 *   viewer.load(pdfUrl);
 *   viewer.destroy();
 */
(function (window) {
  'use strict';

  var PDFJS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  var PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  var PAGEFLIP_SRC = 'https://cdn.jsdelivr.net/npm/page-flip@2.0.7/dist/js/page-flip.browser.js';

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) {
        resolve();
        return;
      }
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = function () {
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function ensureLibs() {
    return loadScript(PAGEFLIP_SRC).then(function () {
      return loadScript(PDFJS_SRC);
    }).then(function () {
      if (!window.pdfjsLib || !window.St || !window.St.PageFlip) {
        throw new Error('Flipbook libraries failed to load');
      }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    });
  }

  function AtlasPdfFlipbook(root, options) {
    this.root = root;
    this.options = options || {};
    this.stageEl = root.querySelector('[data-apf-stage]');
    this.bookWrapEl = root.querySelector('.atlas-pdf-flipbook__book-wrap');
    this.bookEl = root.querySelector('[data-apf-book]');
    this.statusEl = root.querySelector('[data-apf-status]');
    this.statusText = root.querySelector('[data-apf-status-text]');
    this.progressEl = root.querySelector('[data-apf-progress]');
    this.pagerEl = root.querySelector('[data-apf-pager]');
    this.prevBtn = root.querySelector('[data-apf-prev]');
    this.nextBtn = root.querySelector('[data-apf-next]');
    this.emptyEl = root.querySelector('[data-apf-empty]');
    this.thumbsEl = root.querySelector('[data-apf-thumbs]');
    this.showThumbnails =
      this.options.showThumbnails === true ||
      root.getAttribute('data-apf-show-thumbs') === 'true';
    this.pageFlip = null;
    this.pageCount = 0;
    this.blobUrls = [];
    this.loadToken = 0;
    this.emptyMessage = this.options.emptyMessage || 'Add a PDF URL to load the catalogue.';
    this.boundKeydown = this.onKeydown.bind(this);
    this.keysEnabled = false;

    this.bindControls();
  }

  AtlasPdfFlipbook.prototype.bindControls = function () {
    var self = this;
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.prev();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.next();
      });
    }
  };

  AtlasPdfFlipbook.prototype.enableKeys = function () {
    if (this.keysEnabled) return;
    this.keysEnabled = true;
    document.addEventListener('keydown', this.boundKeydown);
  };

  AtlasPdfFlipbook.prototype.disableKeys = function () {
    if (!this.keysEnabled) return;
    this.keysEnabled = false;
    document.removeEventListener('keydown', this.boundKeydown);
  };

  AtlasPdfFlipbook.prototype.onKeydown = function (event) {
    if (!this.pageFlip) return;
    if (event.key === 'ArrowRight') this.next();
    if (event.key === 'ArrowLeft') this.prev();
    if (event.key === 'Home') this.goTo(0);
    if (event.key === 'End') this.goTo(this.pageCount - 1);
  };

  AtlasPdfFlipbook.prototype.setProgress = function (current, total) {
    if (this.progressEl) this.progressEl.style.width = Math.round((current / total) * 100) + '%';
    if (this.statusText) this.statusText.textContent = 'Loading page ' + current + ' of ' + total;
  };

  AtlasPdfFlipbook.prototype.setPager = function (index) {
    var current = index + 1;
    if (this.pagerEl) this.pagerEl.textContent = 'Page ' + current + ' of ' + this.pageCount;
    if (this.prevBtn) this.prevBtn.disabled = index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = index >= this.pageCount - 1;

    if (this.thumbsEl) {
      this.thumbsEl.querySelectorAll('button').forEach(function (btn, i) {
        btn.classList.toggle('is-active', i === index);
      });
    }
  };

  AtlasPdfFlipbook.prototype.clearThumbs = function () {
    if (!this.thumbsEl) return;
    this.thumbsEl.innerHTML = '';
    this.thumbsEl.hidden = true;
  };

  AtlasPdfFlipbook.prototype.createThumb = function (src, index) {
    var self = this;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'atlas-pdf-flipbook__thumb';
    btn.setAttribute('aria-label', 'Go to page ' + (index + 1));

    var img = document.createElement('img');
    img.alt = '';
    img.src = src;
    img.draggable = false;

    var label = document.createElement('span');
    label.textContent = String(index + 1);

    btn.appendChild(img);
    btn.appendChild(label);
    btn.addEventListener('click', function () {
      self.goTo(index);
    });

    return btn;
  };

  AtlasPdfFlipbook.prototype.revokeBlobs = function () {
    this.blobUrls.forEach(function (url) {
      URL.revokeObjectURL(url);
    });
    this.blobUrls = [];
  };

  AtlasPdfFlipbook.prototype.resetBookElement = function () {
    if (this.bookWrapEl) {
      this.bookWrapEl.style.width = '';
      this.bookWrapEl.style.height = '';

      // PageFlip.destroy() removes the book node from the DOM.
      // Always rebuild it inside the wrap — never replaceWith on a detached node.
      Array.prototype.slice.call(this.bookWrapEl.children).forEach(function (child) {
        if (!child.hasAttribute('data-apf-status')) child.remove();
      });
    }

    var fresh = document.createElement('div');
    fresh.className = 'atlas-pdf-flipbook__book';
    fresh.setAttribute('data-apf-book', '');

    if (this.bookWrapEl) {
      var status = this.bookWrapEl.querySelector('[data-apf-status]');
      if (status) this.bookWrapEl.insertBefore(fresh, status);
      else this.bookWrapEl.appendChild(fresh);
    }

    this.bookEl = fresh;
  };

  AtlasPdfFlipbook.prototype.cleanup = function () {
    if (this.pageFlip) {
      try {
        // Removes the book element from the DOM — resetBookElement re-inserts it.
        this.pageFlip.destroy();
      } catch (e) {}
      this.pageFlip = null;
    }
    this.pageCount = 0;
    this.revokeBlobs();
    this.resetBookElement();
    this.clearThumbs();

    if (this.progressEl) this.progressEl.style.width = '0%';
    if (this.statusEl) this.statusEl.hidden = false;
    if (this.statusText) this.statusText.textContent = 'Loading catalogue';
    if (this.pagerEl) this.pagerEl.textContent = 'Page 0 of 0';
    if (this.prevBtn) this.prevBtn.disabled = true;
    if (this.nextBtn) this.nextBtn.disabled = true;
  };

  AtlasPdfFlipbook.prototype.destroy = function () {
    this.disableKeys();
    this.showEmpty(this.emptyMessage);
  };

  AtlasPdfFlipbook.prototype.showEmpty = function (message) {
    this.loadToken += 1;
    this.cleanup();
    if (this.stageEl) this.stageEl.hidden = true;
    if (this.emptyEl) {
      this.emptyEl.hidden = false;
      this.emptyEl.textContent = message || this.emptyMessage;
    }
  };

  AtlasPdfFlipbook.prototype.showStage = function () {
    if (this.emptyEl) this.emptyEl.hidden = true;
    if (this.stageEl) this.stageEl.hidden = false;
  };

  AtlasPdfFlipbook.prototype.prepareBookSize = function () {
    var self = this;
    return new Promise(function (resolve) {
      var tries = 0;

      function apply() {
        if (!self.bookEl || !self.bookEl.isConnected) {
          self.resetBookElement();
        }

        if (!self.bookWrapEl || !self.bookEl) {
          resolve({ width: 800, height: 450 });
          return;
        }

        // Clear previous inline sizing so layout can compute naturally first.
        self.bookWrapEl.style.width = '';
        self.bookWrapEl.style.height = '';

        var rootRect = self.root.getBoundingClientRect();
        var availableWidth = Math.max(rootRect.width - 40, 280);
        var width = Math.min(availableWidth, 980);
        var height = Math.round(width * (9 / 16));
        var maxHeight = Math.min(
          window.innerHeight * (self.showThumbnails && self.thumbsEl ? 0.48 : 0.58),
          self.showThumbnails && self.thumbsEl ? 480 : 560
        );

        if (height > maxHeight) {
          height = Math.max(Math.round(maxHeight), 180);
          width = Math.round(height * (16 / 9));
        }

        // Hard pixel box — PageFlip stretch mode needs real width/height.
        self.bookWrapEl.style.width = width + 'px';
        self.bookWrapEl.style.height = height + 'px';
        self.bookEl.style.width = '100%';
        self.bookEl.style.height = '100%';

        var measuredW = self.bookEl.clientWidth;
        var measuredH = self.bookEl.clientHeight;

        if ((measuredW > 40 && measuredH > 40) || tries >= 40) {
          resolve({
            width: measuredW > 40 ? measuredW : width,
            height: measuredH > 40 ? measuredH : height,
          });
          return;
        }

        tries += 1;
        requestAnimationFrame(apply);
      }

      requestAnimationFrame(apply);
    });
  };

  AtlasPdfFlipbook.prototype.createSheet = function (src, index) {
    var sheet = document.createElement('div');
    sheet.className = 'atlas-pdf-flipbook__sheet';
    var img = document.createElement('img');
    img.alt = 'Catalogue page ' + (index + 1);
    img.src = src;
    img.draggable = false;
    sheet.appendChild(img);
    return sheet;
  };

  AtlasPdfFlipbook.prototype.renderPage = function (pdf, pageNumber, width) {
    return pdf.getPage(pageNumber).then(function (page) {
      var base = page.getViewport({ scale: 1 });
      var dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      var scale = (width * dpr) / base.width;
      var viewport = page.getViewport({ scale: scale });
      var canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      return page
        .render({ canvasContext: canvas.getContext('2d'), viewport: viewport })
        .promise.then(function () {
          return new Promise(function (resolve) {
            canvas.toBlob(
              function (blob) {
                canvas.width = 0;
                canvas.height = 0;
                resolve(URL.createObjectURL(blob));
              },
              'image/jpeg',
              0.86
            );
          });
        });
    });
  };

  AtlasPdfFlipbook.prototype.initFlipbook = function (sheets, size) {
    var self = this;
    if (!window.St || !window.St.PageFlip) return;

    if (!this.bookEl || !this.bookEl.isConnected) {
      this.resetBookElement();
    }
    if (!this.bookEl) return;

    var width = Math.max((size && size.width) || this.bookEl.clientWidth || 800, 320);
    var height = Math.max((size && size.height) || this.bookEl.clientHeight || Math.round(width * 9 / 16), 180);

    this.pageFlip = new window.St.PageFlip(this.bookEl, {
      width: Math.round(width),
      height: Math.round(height),
      size: 'stretch',
      minWidth: Math.floor(width / 2) + 1,
      maxWidth: Math.round(width),
      minHeight: Math.floor(height / 2) + 1,
      maxHeight: Math.round(height),
      drawShadow: true,
      flippingTime: 800,
      usePortrait: true,
      autoSize: true,
      maxShadowOpacity: 0.35,
      showCover: true,
      mobileScrollSupport: true,
      swipeDistance: 30,
      useMouseEvents: true,
      showPageCorners: true,
    });

    this.pageFlip.loadFromHTML(sheets);
    this.pageFlip.on('flip', function (e) {
      self.setPager(e.data);
    });
    this.setPager(0);
  };

  AtlasPdfFlipbook.prototype.load = function (url) {
    var self = this;
    if (!url) {
      this.showEmpty(this.emptyMessage);
      return Promise.resolve();
    }

    this.loadToken += 1;
    var token = this.loadToken;
    this.cleanup();
    this.showStage();
    if (this.statusEl) this.statusEl.hidden = false;
    if (this.statusText) this.statusText.textContent = 'Loading catalogue';

    var bookSize = null;

    return this.prepareBookSize()
      .then(function (size) {
        if (token !== self.loadToken) return null;
        bookSize = size;
        return ensureLibs();
      })
      .then(function () {
        if (token !== self.loadToken) return null;
        return window.pdfjsLib.getDocument({ url: url, withCredentials: false }).promise;
      })
      .then(function (pdf) {
        if (!pdf || token !== self.loadToken) return;

        self.pageCount = pdf.numPages;
        var renderWidth = Math.min(Math.max((bookSize && bookSize.width) || 900, 640), 1400);
        var sheets = [];
        var chain = Promise.resolve();

        for (var i = 1; i <= self.pageCount; i += 1) {
          (function (pageNumber) {
            chain = chain.then(function () {
              if (token !== self.loadToken) return;
              self.setProgress(pageNumber, self.pageCount);
              return self.renderPage(pdf, pageNumber, renderWidth).then(function (blobUrl) {
                if (token !== self.loadToken) {
                  URL.revokeObjectURL(blobUrl);
                  return;
                }
                self.blobUrls.push(blobUrl);
                sheets.push(self.createSheet(blobUrl, pageNumber - 1));
                if (self.showThumbnails && self.thumbsEl) {
                  self.thumbsEl.appendChild(self.createThumb(blobUrl, pageNumber - 1));
                }
                if (pageNumber === 1) {
                  self.initFlipbook(sheets, bookSize);
                  if (self.prevBtn) self.prevBtn.disabled = true;
                  if (self.nextBtn) self.nextBtn.disabled = self.pageCount < 2;
                } else if (self.pageFlip) {
                  self.pageFlip.updateFromHtml(sheets);
                }
              });
            });
          })(i);
        }

        return chain.then(function () {
          if (token !== self.loadToken) return;
          if (self.statusEl) self.statusEl.hidden = true;
          if (self.showThumbnails && self.thumbsEl) self.thumbsEl.hidden = false;
          if (self.pageFlip) self.setPager(self.pageFlip.getCurrentPageIndex());
        });
      })
      .catch(function (error) {
        console.error(error);
        if (token !== self.loadToken) return;
        self.showEmpty('The catalogue could not be loaded. Check the PDF URL and try again.');
      });
  };

  AtlasPdfFlipbook.prototype.prev = function () {
    if (this.pageFlip) this.pageFlip.flipPrev();
  };

  AtlasPdfFlipbook.prototype.next = function () {
    if (this.pageFlip) this.pageFlip.flipNext();
  };

  AtlasPdfFlipbook.prototype.goTo = function (index) {
    if (this.pageFlip && index >= 0 && index < this.pageCount) this.pageFlip.flip(index);
  };

  AtlasPdfFlipbook.mount = function (root, options) {
    if (!root) return null;
    return new AtlasPdfFlipbook(root, options);
  };

  window.AtlasPdfFlipbook = AtlasPdfFlipbook;
})(window);
